import { Loader } from '@pwc/pwc-deals-ui';
import { UserUtilities } from '@pwc/pwc-deals-web-common';
import { useGetCurrentUser } from 'common/hooks/useGetCurrentUser';
import {
  getCommentPanelOpened,
  getComments,
  getCommentsIsLoading,
  getHasCommentSelection,
  getOpenNewCommentThread,
  getViewingStatus,
  getCurrentCommentThreads,
  setCommentPanelOpened,
  viewResolvedComments,
  viewUnresolvedComments,
} from 'features/comments/commentsSlice';
import { COMMENT_VIEWING_STATUS } from 'features/comments/constants';
import { useDispatch, useSelector } from 'react-redux';
import CommentPanelActions from '../CommentPanel/CommentPanelActions';
import CommentPanelHeader from '../CommentPanel/CommentPanelHeader';
import { RibbonButtonTypeProps } from '../CommentPanel/RibbonPanelButton';
import './RibbonPanel.scss';

interface Props {
  projectId: number;
  isProjectDisabled: boolean;
  onClose: () => void;
  handleResolveAllClick: () => void;
}

const PageLevelCommentPanel = ({
  isProjectDisabled,
  projectId,
  onClose,
  handleResolveAllClick,
}: Props) => {
  const dispatch = useDispatch();

  const { isEntitySharedWithClient } = useSelector(getComments);
  const commentsIsLoading = useSelector(getCommentsIsLoading) as boolean;
  const commentPanelOpened = useSelector(getCommentPanelOpened) as boolean;
  const viewingStatus = useSelector(getViewingStatus) as
    | 'unresolved'
    | 'resolved';

  const hasCommentSelection = useSelector(getHasCommentSelection) as boolean;
  const commentThreadsFromStore = useSelector(
    getCurrentCommentThreads
  ) as Array<unknown>;
  const openNewCommentThread = useSelector(getOpenNewCommentThread);

  const currentUser = useGetCurrentUser();

  const noUnsresolvedCommentsMessage = isEntitySharedWithClient
    ? "Use this panel to add comments about the project's content."
    : 'Use this panel for comments that only PwC team can see.';

  const currentUserIsPractitioner = UserUtilities.isPractitioner(
    currentUser,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    projectId
  );
  const clientLabel = currentUserIsPractitioner
    ? 'Client Comments'
    : 'Comments';
  const commentCountLabel = isEntitySharedWithClient
    ? clientLabel
    : 'PwC Comments';

  const comments = commentThreadsFromStore;

  const totalComments = Array.isArray(comments) ? comments.length : 0;
  const totalCommentsUnresolved = comments.filter(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (thread: any) => !thread.resolved
  ).length;

  const totalCommentsRendered =
    viewingStatus === COMMENT_VIEWING_STATUS.RESOLVED
      ? totalComments
      : totalCommentsUnresolved;

  const handleResolveClick = () => {
    if (viewingStatus === COMMENT_VIEWING_STATUS.UNRESOLVED) {
      dispatch(viewResolvedComments());
    } else {
      dispatch(viewUnresolvedComments());
    }
  };

  const handlePanelButtonClick = ({ type }: RibbonButtonTypeProps) => {
    if (type === 'comment') {
      if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
        dispatch(viewUnresolvedComments());
      }
    } else if (type === 'preview') {
      // TODO: show live preview content
    }

    dispatch(setCommentPanelOpened(!commentPanelOpened));
  };

  const showNoCommentMessage =
    totalCommentsUnresolved === 0 &&
    totalCommentsRendered === 0 &&
    openNewCommentThread === false;

  const noCommentYet =
    commentPanelOpened && !commentsIsLoading && showNoCommentMessage;

  return (
    <>
      <CommentPanelHeader
        commentPanelOpened={commentPanelOpened}
        commentCountLabel={commentCountLabel}
        totalCommentsRendered={totalCommentsRendered}
        hasCommentSelection={hasCommentSelection}
        handlePanelButtonClick={(type) => handlePanelButtonClick(type)}
        onClose={onClose}
      />
      <div className="ribbon-panel-scroll">
        <CommentPanelActions
          commentPanelOpened={commentPanelOpened}
          totalComments={totalComments}
          totalCommentsUnresolved={totalCommentsUnresolved}
          hasCommentSelection={hasCommentSelection}
          isProjectDisabled={isProjectDisabled}
          handleResolveClick={handleResolveClick}
          handleResolveAllClick={handleResolveAllClick}
          viewingStatus={viewingStatus}
        />
        {noCommentYet && (
          <div data-testid="no-comment-message" className="no-comment-message">
            <div className="no-comment-message__content">
              <p>No comments yet.</p>
              <p>{noUnsresolvedCommentsMessage}</p>
            </div>
          </div>
        )}
        <Loader
          dataTestId="comment-loading-indicator"
          isVisible={commentsIsLoading && commentPanelOpened}
        />
        <div className="top" />
        <div
          className="scrollable"
          id="comment-panel"
          data-testid="comment-panel"
          hidden={!commentPanelOpened}
        />

        <div className="bottom" />
      </div>
    </>
  );
};

export default PageLevelCommentPanel;
